import React from 'react'
import ReactDOM from 'react-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import { updateContractTimeZone, CONTRACT_TIMEZONE_FAILURE } from '../actions/contracts'
import { CONTRACT_TIMEZONES } from '../utils/timezones'
import '../styles/ContractTimeZoneSelect.css'

const DEFAULT_TZ = 'Europe/Madrid'
const MAX_RESULTS = 50

const zoneLabel = (tz) => tz.split('/').pop().replace(/_/g, ' ')

// Nombre de la zona + desfase actual respecto a UTC
const ZoneText = ({ tz }) => (
  <span className="qrs-admin-zone ctz-text" title={tz}>
    {zoneLabel(tz)}
    {tz !== 'UTC' && <span className="ctz-offset">{currentOffset(tz)}</span>}
  </span>
)

// Normaliza para buscar sin tildes ni mayúsculas, y "_" como espacio
const normalize = (text) => String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/_/g, ' ').toLowerCase()

// Desfase actual de la zona ("UTC+1"), como pista en los resultados
const currentOffset = (tz) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')
    return part ? part.value.replace('GMT', 'UTC').replace(/^UTC$/, 'UTC+0') : ''
  } catch (e) {
    return ''
  }
}

// Catálogo: primero las zonas habituales de contratos (con su etiqueta en
// español), después el resto de zonas IANA que soporte el navegador
const buildCatalog = () => {
  const curated = CONTRACT_TIMEZONES.map(tz => ({ value: tz.value, label: tz.label }))
  // UTC puro (sin horario de verano); los navegadores no lo listan en supportedValuesOf
  if (!curated.some(tz => tz.value === 'UTC')) curated.push({ value: 'UTC', label: 'UTC (sin horario de verano)' })
  const known = new Set(curated.map(tz => tz.value))
  let all = []
  try {
    all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  } catch (e) {
    all = []
  }
  const rest = all.filter(tz => !known.has(tz)).map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') }))
  return [...curated, ...rest].map(tz => ({ ...tz, search: normalize(`${tz.label} ${tz.value}`) }))
}

let catalogCache = null
const getCatalog = () => {
  if (!catalogCache) catalogCache = buildCatalog()
  return catalogCache
}

// Zona horaria del contrato editable desde el listado con un buscador. Solo
// toca el servidor (PUT contracts/:id/timezone): no activa ParametersUpdated
// en la máquina.
class ContractTimeZoneSelect extends React.Component {
  constructor(props) {
    super(props)
    this.state = { saving: false, open: false, query: '', highlight: 0, rect: null }
    this.inputRef = React.createRef()
  }

  componentWillUnmount() {
    this.unlisten()
  }

  listen() {
    // El panel va en position:fixed; si la página se mueve se cierra
    window.addEventListener('scroll', this.close, true)
    window.addEventListener('resize', this.close)
  }

  unlisten() {
    window.removeEventListener('scroll', this.close, true)
    window.removeEventListener('resize', this.close)
  }

  open = (e) => {
    e.stopPropagation()
    if (this.state.saving) return
    const rect = e.currentTarget.getBoundingClientRect()
    this.setState({ open: true, query: '', highlight: 0, rect }, () => {
      if (this.inputRef.current) this.inputRef.current.focus()
      this.listen()
    })
  }

  close = (e) => {
    // El scroll de la propia lista de resultados no cierra
    if (e && e.type === 'scroll' && e.target && e.target.classList && e.target.classList.contains('ctz-results')) return
    this.unlisten()
    this.setState({ open: false, query: '' })
  }

  results() {
    const words = normalize(this.state.query).split(/\s+/).filter(Boolean)
    const catalog = getCatalog()
    const matches = words.length ? catalog.filter(tz => words.every(w => tz.search.includes(w))) : catalog
    return matches.slice(0, MAX_RESULTS)
  }

  choose(value) {
    const { contract, updateContractTimeZone } = this.props
    const current = contract.TimeZone || DEFAULT_TZ
    this.close()
    if (!value || value === current) return

    this.setState({ saving: true })
    updateContractTimeZone(contract._id, value).then(result => {
      this.setState({ saving: false })
      if (result && result.type === CONTRACT_TIMEZONE_FAILURE) {
        window.alert(`No se pudo cambiar la zona horaria: ${(result.error && result.error.message) || 'error desconocido'}`)
      }
    })
  }

  handleKeyDown = (e, results) => {
    const { highlight } = this.state
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState({ highlight: Math.min(highlight + 1, results.length - 1) })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState({ highlight: Math.max(highlight - 1, 0) })
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (results[highlight]) this.choose(results[highlight].value)
    } else if (e.key === 'Escape') {
      this.close()
    }
  }

  renderPanel(current) {
    const { query, highlight, rect } = this.state
    const results = this.results()
    const width = Math.max(rect.width, 280)
    const left = Math.min(rect.left, window.innerWidth - width - 8)
    const openUp = rect.bottom + 320 > window.innerHeight && rect.top > 320
    const style = openUp
      ? { left, width, bottom: window.innerHeight - rect.top + 4 }
      : { left, width, top: rect.bottom + 4 }

    return ReactDOM.createPortal(
      <div className="ctz-panel" style={style} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        <div className="ctz-search">
          <i className="fas fa-search"></i>
          <input
            ref={this.inputRef}
            type="text"
            value={query}
            placeholder="Buscar zona, ciudad o país…"
            onChange={e => this.setState({ query: e.target.value, highlight: 0 })}
            onKeyDown={e => this.handleKeyDown(e, results)}
            onBlur={() => this.close()}
          />
        </div>
        <ul className="ctz-results">
          {results.length === 0 && <li className="ctz-empty">Sin resultados</li>}
          {results.map((tz, i) => (
            <li
              key={tz.value}
              className={`ctz-option${i === highlight ? ' active' : ''}${tz.value === current ? ' current' : ''}`}
              // mousedown + preventDefault: elige antes del blur del input
              onMouseDown={e => { e.preventDefault(); this.choose(tz.value) }}
              onMouseEnter={() => this.setState({ highlight: i })}
            >
              <span className="ctz-option-label">{tz.label}</span>
              <span className="ctz-option-offset">{currentOffset(tz.value)}</span>
              {tz.value === current && <i className="fas fa-check ctz-option-check"></i>}
            </li>
          ))}
        </ul>
      </div>,
      document.body
    )
  }

  render() {
    const { contract, role } = this.props
    const { saving, open } = this.state
    const current = contract.TimeZone || DEFAULT_TZ

    // Contrato terminado: solo lectura
    if (role !== 'ADMIN' || contract.Status === 'TERMINATED') {
      return <ZoneText tz={current} />
    }

    return (
      <div className="ctz-value">
        <ZoneText tz={current} />
        <button
          type="button"
          className={`ctz-edit-btn${open ? ' open' : ''}`}
          title="Cambiar zona horaria"
          disabled={saving}
          onClick={this.open}
        >
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-pen'}`}></i>
        </button>
        {open && this.renderPanel(current)}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role
})

const mapDispatchToProps = dispatch => ({
  updateContractTimeZone: bindActionCreators(updateContractTimeZone, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(ContractTimeZoneSelect)
